"use client";

import { c as _c } from "react/compiler-runtime";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { useEffect, useMemo, useRef, useState } from "react";

// One `loadStripe` per (key, account) for the whole app - the payment surface
// owns that cache and a second one would load the script twice.
import { getStripe } from "@/components/payments/stripe-payment-surface";
import { useStripeAppearance } from "@/components/payments/use-stripe-appearance";
import RainforestSkeleton from "@/components/rainforest/rainforest-skeleton";
import { Button } from "@/components/ui";
import { assertMountedSecret } from "@/lib/payments/assert-mounted-secret";
import { DeclineAudience, normalizeDecline } from "@/lib/payments/decline-reason";
import { registerPaymentSurface } from "@/lib/payments/payment-surface-registry";
import { isSessionAlreadyConsumed } from "@/lib/payments/session-consumed";
import { useSurfaceLoadRetry } from "@/lib/payments/use-surface-load-retry";
import { cx } from "@/lib/tailwind-utils";
import { PaymentProcessor } from "@/types/auth";

/** Narrow an unknown to something carrying a string `id`. */
const isRecordWithId = (value: unknown): value is {
  id: string;
} => typeof value === "object" && value !== null && typeof (value as {
  id?: unknown;
}).id === "string";

/**
 * Saving a card on Stripe: a SetupIntent, confirmed with no amount.
 *
 * The sibling of `StripePaymentSurface`, and separate from it for one reason
 * that is not cosmetic. Taking a payment confirms a PaymentIntent and reports
 * an intent id; saving a card confirms a *SetupIntent* and reports a
 * **payment method** id, which is what the caller vaults. Sharing one component
 * would mean a union return type and a branch at every call site, which is the
 * branching the surfaces exist to remove.
 *
 * `usage: "off_session"` is set server-side when the intent is minted, because
 * the cards saved here are charged later by payment plans and memberships with
 * nobody at the keyboard.
 */

interface StripeSaveMethodSurfaceProps {
  /**
   * Who reads the failure. Defaults to staff, because most of the call sites
   * are console surfaces; the patient-facing ones - portal add-card,
   * self-schedule, the plan link, the embed, the form payment block - pass
   * "patient" so a refusal is not reported to the cardholder as "ask the
   * patient to contact their financial institution".
   */
  audience?: DeclineAudience;
  publishableKey: string;
  stripeAccount: string;
  /** The SetupIntent's client secret. */
  clientSecret: string;
  /** Called with the Stripe payment method id to vault. */
  onSuccess: (processorPaymentMethodId: string) => void;
  onFailure: (message: string) => void;
  onValid?: () => void;
  onInvalid?: () => void;
  className?: string;
  /**
   * False means "render your own Save button", which is what ten of the call
   * sites ask for - the add-payment-method modal, the embed, the self-schedule
   * step and the rest render no button of their own and rely on the surface
   * having one. Rainforest's web component honours this; without it here, a
   * Stripe merchant got a card form with nothing to submit it.
   */
  hideButton?: boolean;
}
function StripeSaveMethodForm({
  audience,
  clientSecret,
  onSuccess,
  onFailure,
  onValid,
  onInvalid,
  hideButton,
  onLoaded,
  onRetry,
  didNotLoad
}: Pick<StripeSaveMethodSurfaceProps, "audience" | "clientSecret" | "onSuccess" | "onFailure" | "onValid" | "onInvalid" | "hideButton"> & {
  onLoaded: () => void;
  onRetry: () => void;
  didNotLoad: boolean;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [isReady, setIsReady] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Captured once. The enclosing `Elements` group is keyed by the secret, so a
  // re-minted SetupIntent remounts this component and this ref with it. If the
  // key is ever weakened, this keeps the old secret while the prop moves on and
  // the assert below fires rather than confirming a spent intent.
  const mountedSecret = useRef(clientSecret).current;
  const latest = useRef({
    stripe,
    elements,
    clientSecret,
    onSuccess,
    onFailure,
    audience
  });
  latest.current = {
    stripe,
    elements,
    clientSecret,
    onSuccess,
    onFailure,
    audience
  };
  const inFlight = useRef(false);
  const failedToLoad = useRef(false);
  failedToLoad.current = didNotLoad && !isReady;

  // One implementation, two callers: the registry (an external Save button
  // elsewhere on the page) and the built-in button below. A second copy for the
  // button would be a second place for the spent-secret assert to be forgotten.
  const submit = useRef(() => {
    const current = latest.current;
    if (inFlight.current) return;
    if (failedToLoad.current) {
      current.onFailure("The card form did not load. Use Try again to reload it.");
      return;
    }
    if (!current.stripe || !current.elements) {
      current.onFailure("The card form is still loading. Please try again in a moment.");
      return;
    }
    try {
      assertMountedSecret(mountedSecret, current.clientSecret);
    } catch (error) {
      current.onFailure(error instanceof Error ? error.message : "The card form is out of step with this request. Please reload and try again.");
      return;
    }
    inFlight.current = true;
    setIsSaving(true);
    void current.stripe.confirmSetup({
      elements: current.elements,
      // Nothing here redirects: the intent is minted with
      // `allow_redirects: "never"`, so a method that would redirect is
      // never offered.
      redirect: "if_required"
    }).then(({
      error: error_0,
      setupIntent
    }) => {
      if (error_0) {
        // Already confirmed - a remount, or a second Save once `inFlight`
        // reset. The card is saved; the second confirm is the thing that
        // failed, and calling it a decline sends the patient to re-enter a
        // card we are already holding.
        if (isSessionAlreadyConsumed(PaymentProcessor.STRIPE, {
          error: error_0
        })) {
          const settled = (error_0 as {
            setup_intent?: {
              payment_method?: unknown;
            };
          }).setup_intent?.payment_method;
          const settledId = typeof settled === "string" ? settled : isRecordWithId(settled) ? settled.id : null;
          if (settledId) {
            current.onSuccess(settledId);
            return;
          }
        }
        current.onFailure(normalizeDecline(PaymentProcessor.STRIPE, {
          error: error_0
        }, current.audience,
        // Nothing was charged here, so the tables' "No charge was made."
        // closing sentence is replaced with "The card was not saved."
        "save").message);
        return;
      }
      const method = setupIntent?.payment_method;
      if (!method) {
        current.onFailure("We could not save this card. Please check the details and try again.");
        return;
      }
      // A confirmed SetupIntent carries either the id or the expanded
      // object, depending on how it was created. The caller vaults an
      // id either way.
      current.onSuccess(typeof method === "string" ? method : method.id);
    }).catch(() => {
      current.onFailure("We could not reach the payment processor. Please try again.");
    }).finally(() => {
      inFlight.current = false;
      setIsSaving(false);
    });
  }).current;
  useEffect(() => registerPaymentSurface({
    submit
  }), [submit]);
  if (didNotLoad && !isReady) {
    return <div className="flex flex-col items-start gap-2 py-4">
        <p className="text-body text-status-error-text">
          The card form did not finish loading.
        </p>
        <Button variant="primary" onClick={onRetry}>
          <span>Try again</span>
        </Button>
      </div>;
  }
  return <>
      {!isReady && <RainforestSkeleton padding="md" />}
      <div className={cx(!isReady && "hidden")}>
        <PaymentElement options={{
        // Rainforest renders the card fields immediately; the Payment
        // Element defaults to an accordion with every method collapsed, so
        // on a Stripe merchant staff had to click "Card" before they could
        // type. Expanding the first item is the closer match, and with one
        // method configured Stripe renders it expanded either way.
        layout: {
          type: "accordion",
          defaultCollapsed: false
        },
        // Pinned here too. The SetupIntent is minted with
        // `automatic_payment_methods`, so what this form offers follows
        // each clinic's Stripe settings - a clinic with bank accounts on
        // gets "US bank account" as well, in an order Stripe picks. This
        // only sets the order: methods it does not name still show, after
        // card, so it hides nothing.
        paymentMethodOrder: ["card"],
        // Saving a card is a card-only flow: a wallet here would vault a
        // device token the off-session charge cannot reliably reuse. Link
        // is the same argument and not a separate policy - it vaults a Link
        // payment method, and the plans and memberships that charge these
        // cards run with nobody at the keyboard to authenticate one. So it
        // is suppressed here outright rather than gated per surface the way
        // `StripePaymentSurface` gates it.
        wallets: {
          applePay: "never",
          googlePay: "never",
          link: "never"
        }
      }} onReady={() => {
        setIsReady(true);
        onLoaded();
      }} onChange={event => {
        if (event.complete) onValid?.();else onInvalid?.();
      }} />
        {!hideButton && <Button variant="primary" className="mt-4 w-full" onClick={submit} disabled={isSaving} aria-busy={isSaving}>
            <span>{isSaving ? "Saving…" : "Save Payment Method"}</span>
          </Button>}
      </div>
    </>;
}
export default function StripeSaveMethodSurface(t0) {
  const $ = _c(27);
  const {
    audience,
    publishableKey,
    stripeAccount,
    clientSecret,
    onSuccess,
    onFailure,
    onValid,
    onInvalid,
    className,
    hideButton: t1
  } = t0;
  const hideButton = t1 === undefined ? true : t1;
  let t2;
  if ($[0] !== publishableKey || $[1] !== stripeAccount) {
    t2 = getStripe(publishableKey, stripeAccount);
    $[0] = publishableKey;
    $[1] = stripeAccount;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const stripePromise = t2;
  const {
    attempt,
    didNotLoad,
    markLoaded,
    retry
  } = useSurfaceLoadRetry(clientSecret);
  const appearance = useStripeAppearance();
  let t3;
  if ($[3] !== className) {
    t3 = cx("w-full", className);
    $[3] = className;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const t4 = `${clientSecret}::${attempt}`;
  let t5;
  if ($[5] !== appearance || $[6] !== clientSecret) {
    t5 = {
      clientSecret,
      appearance
    };
    $[5] = appearance;
    $[6] = clientSecret;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== audience || $[9] !== clientSecret || $[10] !== didNotLoad || $[11] !== hideButton || $[12] !== markLoaded || $[13] !== onFailure || $[14] !== onInvalid || $[15] !== onSuccess || $[16] !== onValid || $[17] !== retry) {
    t6 = <StripeSaveMethodForm audience={audience} clientSecret={clientSecret} onSuccess={onSuccess} onFailure={onFailure} onValid={onValid} onInvalid={onInvalid} hideButton={hideButton} onLoaded={markLoaded} onRetry={retry} didNotLoad={didNotLoad} />;
    $[8] = audience;
    $[9] = clientSecret;
    $[10] = didNotLoad;
    $[11] = hideButton;
    $[12] = markLoaded;
    $[13] = onFailure;
    $[14] = onInvalid;
    $[15] = onSuccess;
    $[16] = onValid;
    $[17] = retry;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== stripePromise || $[20] !== t4 || $[21] !== t5 || $[22] !== t6) {
    t7 = <Elements key={t4} stripe={stripePromise} options={t5}>{t6}</Elements>;
    $[19] = stripePromise;
    $[20] = t4;
    $[21] = t5;
    $[22] = t6;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[24] !== t3 || $[25] !== t7) {
    t8 = <div className={t3}>{t7}</div>;
    $[24] = t3;
    $[25] = t7;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  return t8;
}